/**
 * useAuthSession Hook
 *
 * Hook that syncs NextAuth session with Legend-State auth store
 * This ensures the auth store stays in sync with NextAuth
 */

import { useEffect, useCallback } from 'react';
import { useSession, signOut as nextAuthSignOut } from 'next-auth/react';
import { authStore$, syncAuthWithSession, clearAuthData } from '../stores/auth.store';

/**
 * useAuthSession hook return type
 */
export interface UseAuthSessionReturn {
    // Session state
    status: 'loading' | 'authenticated' | 'unauthenticated';
    isAuthenticated: boolean;
    isLoading: boolean;

    // Actions
    signOut: () => Promise<void>;
}

/**
 * useAuthSession hook
 *
 * Syncs NextAuth session with Legend-State auth store
 * Use this at the app root level to keep stores in sync
 *
 * @example
 * ```tsx
 * // In _app.tsx or layout
 * function App({ children }) {
 *   const { status } = useAuthSession();
 *
 *   if (status === 'loading') {
 *     return <LoadingSpinner />;
 *   }
 *
 *   return children;
 * }
 * ```
 */
export const useAuthSession = (): UseAuthSessionReturn => {
    const { data: session, status } = useSession();

    // Sync session with auth store
    useEffect(() => {
        if (status === 'loading') {
            authStore$.isLoading.set(true);
            return;
        }

        authStore$.isLoading.set(false);

        if (status === 'authenticated' && session) {
            // Sync session data to Legend-State store
            syncAuthWithSession({
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                user: (session as any).user,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                token: (session as any).token,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                expires_at: (session as any).expires_at,
            });
        } else {
            clearAuthData();
        }
    }, [session, status]);

    // Sign out action
    const signOut = useCallback(async () => {
        clearAuthData();
        await nextAuthSignOut({ redirect: false });
    }, []);

    return {
        status,
        isAuthenticated: status === 'authenticated',
        isLoading: status === 'loading',
        signOut,
    };
};
